import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import branchApi from "../../services/branchapi"; // adjust path if needed

const formatBytes = (bytes) => {
  if (bytes === 0 || bytes == null) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const DownloadInventoryCsv = () => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [totalSize, setTotalSize] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const token = localStorage.getItem("adminToken");

  // Fetch branches for dropdown
  useEffect(() => {
    const loadBranches = async () => {
      try {
        const res = await branchApi.getAllBranches({ limit: 1000, page: 0 });
        // Try common shapes safely
        const list =
          res?.data?.data?.result ||
          res?.data?.result ||
          res?.result ||
          res?.data ||
          [];
        setBranches(list);
      } catch (err) {
        console.error("Error loading branches:", err);
        toast.error("Failed to load branches");
      }
    };
    loadBranches();
  }, []);

  const downloadCSV = async () => {
    if (!selectedBranchId) {
      toast.error("Please select a branch first.");
      return;
    }

    setLoading(true);
    setProgress(0);
    setTotalSize(null);

    try {
      const url = `${process.env.REACT_APP_BASE_URL}/downloads/inventory?branchId=${encodeURIComponent(
        selectedBranchId
      )}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
          Expires: "0",
          Authorization: `${token}`, // add Bearer if your backend expects it: `Bearer ${token}`
        },
      });

      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }
      if (!response.body) throw new Error("ReadableStream not supported.");

      const contentLength = response.headers.get("Content-Length");
      const total = contentLength ? parseInt(contentLength, 10) : null;
      setTotalSize(total);

      const reader = response.body.getReader();
      let receivedLength = 0;
      const chunks = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        receivedLength += value.length;

        if (total) {
          const percent = Math.round((receivedLength * 100) / total);
          setProgress(percent);
        } else {
          setProgress(receivedLength);
        }
      }

      const blob = new Blob(chunks, { type: "text/csv" });
      const objUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objUrl;
      link.setAttribute(
        "download",
        `inventory_export_branch_${selectedBranchId}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objUrl);

      toast.success("Inventory CSV downloaded successfully.");
    } catch (error) {
      console.error("Error downloading inventory:", error);
      toast.error("Failed to download inventory file.");
    } finally {
      setLoading(false);
      setProgress(null);
      setTotalSize(null);
    }
  };

  const renderBranchName = (b) =>
    b?.branchName || b?.name || b?.title || `Branch #${b?.branchId ?? b?.id}`;

  const getBranchValue = (b) => String(b?.branchId ?? b?.id ?? "");

  return (
    <div className="catalog-inventory-export">
      <select
        className="form-select"
        aria-label="Select branch for inventory export"
        value={selectedBranchId}
        onChange={(e) => setSelectedBranchId(e.target.value)}
        disabled={loading}
      >
        <option value="">Select Branch</option>
        {branches.map((b) => (
          <option key={getBranchValue(b)} value={getBranchValue(b)}>
            {renderBranchName(b)}
          </option>
        ))}
      </select>

      <button className="btn btn-success" onClick={downloadCSV} disabled={loading || !selectedBranchId}>
        {loading ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            Downloading…{" "}
            {totalSize
              ? `${progress ?? 0}%`
              : progress !== null
              ? formatBytes(progress)
              : "0 Bytes"}
          </>
        ) : (
          "Export Inventory CSV"
        )}
      </button>
    </div>
  );
};

export default DownloadInventoryCsv;
